<template>
  <bean-source-page
    :type="type"
    :columns="columns"
    :form-columns="formColumns"
    :filter-columns="filterColumns"
    :form="form"
    :title="$t('model.user')"
    :actions="actions"
    :batch-actions="batchActions"
    resource="users"
  />
</template>

<script>
import { Vue, Component, Prop } from 'vue-property-decorator';
import { USER_ROLES } from '@/admin_entities/user_detail.js';
import { filterColumnsExtend, columnsExtend, formColumnsExtend } from '@/admin_grapes/users.js';
import { columnsExtend as adminUserColumnsExtend, formColumnsExtend as adminUserFormColumnsExtend } from '@/admin_entities/admin_user_detail.js';
import { columnsExtend as profileColumnsExtend, formColumnsExtend as profileFormColumnsExtend } from '@/admin_entities/profile_detail.js';
import _ from 'lodash';
import RejectFormDialog from './reject.vue';
import { getLabel } from '@/custom_form_columns_extend';
import { USER_PERMISSIONS } from '@/constants';

@Component
export default class Users extends Vue {
  @Prop(String) type;

  // form 新建表单初始化数据（default数据）
  form = {}
  rejectReasons = []

  async mounted() {
    const data = await this.$request.get('/reject_reasons/read_options', { kind_eq: 'admin_reject_apply', page: 0 });

    this.rejectReasons = data
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - 页面类型 enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -页面RESTful api资源名
   */
  filterColumns() {
    const array = [
      'profile_name_cont',
      'profile_phone_cont',
      'role_eq',
      'company_id_eq',
      'state_eq',
      'is_block_eq'
    ].filter(Boolean)

    return filterColumnsExtend(array)
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - 页面类型 enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -页面RESTful api资源名
   */
  columns(h, { type }) {
    const [email] = adminUserColumnsExtend(['email'])
    const [name, phone] = profileColumnsExtend(['name', 'phone'])
    const array = [
      type === 'show' && 'id',
      'avatar',
      'screen_name',
      { ...name, prop: 'profile.name' },
      { ...phone, prop: 'profile.phone' },
      { ...email, prop: 'admin_user.email', minWidth: 100 },
      'role',
      'sales_director',
      'projects',
      'company',
      'remark',
      'state',
      type === 'show' && 'reject_reason',
      'expires_at',
      'is_block',
      'pattern_factory',
      {
        key: 'read_product_cost_price',
        renderCell: (h, { props: { value } }) => this.renderPermissionTag(value, 'read_product_cost_price')
      },
      {
        key: 'update_product_cost_price',
        renderCell: (h, { props: { value } }) => this.renderPermissionTag(value, 'update_product_cost_price')
      },
      {
        key: 'manage_other_product_design',
        renderCell: (h, { props: { value } }) => this.renderPermissionTag(value, 'manage_other_product_design')
      },
      {
        key: 'manage_other_product_design_files',
        renderCell: (h, { props: { value } }) => this.renderPermissionTag(value, 'manage_other_product_design_files')
      },
      {
        key: 'read_other_product_design_cost',
        renderCell: (h, { props: { value } }) => this.renderPermissionTag(value, 'read_other_product_design_cost')
      },
      {
        key: 'read_product_design_statistics',
        renderCell: (h, { props: { value } }) => this.renderPermissionTag(value, 'read_product_design_statistics')
      },
      {
        key: 'read_order_cost',
        renderCell: (h, { props: { value } }) => this.renderPermissionTag(value, 'read_order_cost')
      },
      {
        key: 'read_company',
        renderCell: (h, { props: { value } }) => this.renderPermissionTag(value, 'read_company')
      },
      {
        key: 'read_production_orders',
        renderCell: (h, { props: { value } }) => this.renderPermissionTag(value, 'read_production_orders')
      },
      type === 'show' && 'created_at',
      type === 'show' && 'updated_at',
    ].filter(Boolean)

    return columnsExtend(array)
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - 页面类型 enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -页面RESTful api资源名
   * @param {Object} params.data - 当前表单数据
   * @param {Function} params.change - 调用此方法同步表单数据 change({ field: 'new value' })
   */
  formColumns(h, { data, type, change }) {
    const [email] = adminUserFormColumnsExtend(['email'], { data: data.admin_user, type: type })
    const [name, phone] = profileFormColumnsExtend(['name', 'phone'], { data: data.profile, type: type })

    const array = [
      { ...name, prop: 'profile.name' },
      { ...phone, prop: 'profile.phone' },
      data.admin_uesr && { ...email, prop: 'admin_user.email' },
      {
        prop: 'role',
        label: this.$t('user.role'),
        renderCell: {
          component: 'select',
          props: {
            options: _.map(USER_ROLES.filter(role => role !== 'application_admin_user'), (value) => ({ label: this.$t(`user.roles.${value}`), value: value }))
          },
          on: {
            change: (value) => {
              if (!['supplier_boss', 'supplier'].includes(value)) {
                change({ pattern_factory: null })
              }
            }
          }
        }
      },
      ['supplier_boss', 'supplier'].includes(data.role) && 'company_id',
      data.role === 'salesman' && {
        key: 'sales_director_id',
        renderCell: {
          props: {
            xRemotePreload: async () => {
              const { data } = await this.$request.get('users/read_options', { per_page: 100, role_eq: 'sales_director', is_block_eq: false });
              return data.map(item => ({ value: item.id, label: getLabel({ item, v: { extra: { model_name: 'User' } } }) }));
            },
            xRemoteSearch: async (keyword) => {
              const { data } = await this.$request.get('users/read_options', { keyword, role_eq: 'sales_director', is_block_eq: false });
              return data.map(item => ({ value: item.id, label: getLabel({ item, v: { extra: { model_name: 'User' } } }) }));
            }
          }
        }
      },
      ['salesman', 'sales_director', 'designer'].includes(data.role) && 'project_ids',
      ['supplier_boss', 'supplier'].includes(data.role) && {
        key: 'pattern_factory',
        renderCell: {
          props: {
            clearable: true
          }
        }
      },
      USER_PERMISSIONS.read_product_cost_price.includes(data.role) && 'read_product_cost_price',
      USER_PERMISSIONS.update_product_cost_price.includes(data.role) && 'update_product_cost_price',
      USER_PERMISSIONS.manage_other_product_design.includes(data.role) && {
        key: 'manage_other_product_design',
        hint: '可管理更多制单'
      },
      USER_PERMISSIONS.manage_other_product_design_files.includes(data.role) && {
        key: 'manage_other_product_design_files',
        hint: '可下载更多制单附件'
      },
      USER_PERMISSIONS.read_other_product_design_cost.includes(data.role) && {
        key: 'read_other_product_design_cost',
        hint: '可查看更多制单成本'
      },
      USER_PERMISSIONS.read_product_design_statistics.includes(data.role) && {
        key: 'read_product_design_statistics'
      },
      USER_PERMISSIONS.read_order_cost.includes(data.role) && 'read_order_cost',
      USER_PERMISSIONS.read_company.includes(data.role) && 'read_company',
      USER_PERMISSIONS.read_production_orders.includes(data.role) && {
        key: 'read_production_orders'
      },
    ].filter(Boolean)

    return formColumnsExtend(array, { data: data, type: type })
  }

  /**
   * @param {Object} scope -页面/表格 scope 数据
   * @param {Object} scope.row - 详情页/表格行 数据
   * @param {Object} sourcePageScope
   * @param {Function} sourcePageScope.remove - sourcePage删除方法
   * @param {Function} sourcePageScope.reload - sourcePage重新请求页面数据
   */

  actions({ row }, { remove, reload }) {
    const roleKind = _.get(this.$root.user, 'current_role.role.kind');
    const abilities = _.get(row, 'abilities', {});

    return [
      row.state === 'approved' && _.get(row.admin_user, 'id') !== this.$root.user.id && {
        component: 'routeButton',
        props: {
          label: this.$t('edit'),
          to: { name: 'users.edit', params: { id: row.id } }
        }
      },
      roleKind === 'application' && !row.admin_user && {
        component: 'confirmButton',
        props: {
          label: '绑定微信号',
          can: 'users.bind_admin_user',
          type: 'primary'
        },
        on: {
          confirm: async () =>  {
            await this.$autoLoading(this.$request.put(`/users/${row.id}/bind_admin_user`));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      roleKind === 'application' && _.get(row.admin_user, 'id') === this.$root.user.id && {
        component: 'confirmButton',
        props: {
          label: '解绑微信号',
          can: 'users.bind_admin_user',
          type: 'primary'
        },
        on: {
          confirm: async () =>  {
            await this.$autoLoading(this.$request.put(`/users/${row.id}/unbind_admin_user`));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      abilities.approve && {
        component: 'confirmButton',
        props: {
          label: '通过',
          type: 'primary'
        },
        on: {
          confirm: async () =>  {
            await this.$autoLoading(this.$request.put(`/users/${row.id}/approve`));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      abilities.reject && (
        () => <RejectFormDialog data={row} rejectReasons={this.rejectReasons} submitHandler={async (value) => {
          await this.$autoLoading(this.$request.put(`/users/${row.id}/reject`, value));
          this.$message.success(this.$t('operateSuccess'));
          reload();
        }} />
      ),
      !row.is_block && row.role !== 'application_admin_user' && {
        component: 'confirmButton',
        props: {
          label: this.$t('actions.user.isBlockToTrue'),
          can: 'users.block',
          type: 'primary'
        },
        on: {
          confirm: async () =>  {
            await this.$autoLoading(this.$request.put(`/users/${row.id}/block`));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      row.is_block && {
        component: 'confirmButton',
        props: {
          label: this.$t('actions.user.isBlockToFalse'),
          can: 'users.unblock',
          type: 'primary'
        },
        on: {
          confirm: async () =>  {
            await this.$autoLoading(this.$request.put(`/users/${row.id}/unblock`));
            this.$message.success(this.$t('operateSuccess'));
            reload();
          }
        }
      },
      {
        component: 'confirmButton',
        props: {
          label: this.$t('destroy'),
          type: 'danger',
          can: 'users.destroy'
        },
        on: {
          confirm: () => remove(row.id)
        }
      }
    ].filter(Boolean);
  }

  /**
   * @param {Object} scope -表格 scope 数据
   * @param {Object} scope.selection - 表格已选择行数据
   * @param {Object} sourcePageScope
   * @param {Function} sourcePageScope.remove - sourcePage删除方法
   * @param {Function} sourcePageScope.reload - sourcePage重新请求页面数据
   */
  batchActions({ selection }) {
    const exportClick = async (selection) => {
      if (selection.length === 0) {
        await this.$request.get('/users/export', { ...this.$route.query });
      } else {
        await this.$request.get('/users/export', { ...this.$route.query, id_in: _.map(selection, item => item.id) });
      }

      this.$message.success('文件正在导出，导出成功后将会发送到邮箱');
    }

    return [
      () => <el-button type='primary' onClick={() => exportClick(selection)}>导出</el-button>
    ];
  }

  renderPermissionTag(value, permission) {
    return (
      <div>
        <el-tag type={value ? 'success' : 'danger'}>{value ? '是' : '否'}</el-tag>
        <div style="font-size: 12px; color: #999; margin-top: 5px;">
          仅对{USER_PERMISSIONS[permission].map(role => this.$t(`user.roles.${role}`)).join('、')}角色生效
        </div>
      </div>
    )
  }
}

export const route = [
  {
    path: '/users',
    name: 'users.index',
    component: Users,
    props: {
      type: 'index',
    }
  },
  {
    path: '/users/new',
    name: 'users.new',
    component: Users,
    props: {
      type: 'new',
    }
  },
  {
    path: '/users/:id',
    name: 'users.show',
    component: Users,
    props: {
      type: 'show',
    }
  },
  {
    path: '/users/:id/edit',
    name: 'users.edit',
    component: Users,
    props: {
      type: 'edit'
    }
  }
]
</script>
